import { useState, type FormEvent } from "react";
import {
  SELF_REPORTED_VISION_LABELS,
  VISION_CORRECTION_LABELS,
  assignAgeGroup,
  type PublicStudyDto,
  type SelfReportedVision,
  type VisionCorrection,
} from "@gazelab/shared";
import { Actions, ErrorText, Panel } from "../ui.js";
import { DemoBanner } from "../../../components/ui/DemoBanner.js";

export function WelcomeScreen({ study, onStart }: { study: PublicStudyDto; onStart: () => void }) {
  return (
    <Panel title={study.title} wide>
      {study.demo.enabled && <DemoBanner compact />}
      {study.demo.enabled && study.demo.participationsLeft === 0 && (
        <ErrorText>Esta demostración ya alcanzó el máximo de participaciones. Gracias por tu interés.</ErrorText>
      )}
      {study.description && <p>{study.description}</p>}
      <p>
        Verás <strong>{study.imageCount}</strong>{" "}
        {study.stimulusKind === "video" ? "videos cortos, uno por uno," : study.stimulusKind === "mixed" ? "imágenes y videos cortos, uno por uno," : "fotografías, una por una,"} a pantalla
        completa. Duración estimada:{" "}
        <strong>≈ {study.estimatedMinutes} minutos</strong>, incluida la calibración.
      </p>
      <h2 className="text-lg font-semibold text-slate-900">Qué necesitas</h2>
      <ul className="list-disc space-y-1 pl-6">
        <li>Una computadora de escritorio o portátil con Chrome o Chromium y una webcam.</li>
        <li>Buena iluminación de frente (evita tener una ventana detrás de ti).</li>
        <li>Mantener la cabeza quieta, a unos 50–70 cm de la pantalla.</li>
        <li>Si usas lentes, puedes dejártelos puestos.</li>
      </ul>
      <h2 className="text-lg font-semibold text-slate-900">Sobre la cámara</h2>
      <p>
        La webcam se usa para <strong>estimar</strong> hacia dónde miras en la pantalla. El video se procesa solo en este
        navegador: <strong>no se graba ni se envía</strong> ninguna imagen de tu cara. Solo se guardan coordenadas
        estimadas de la mirada. La cámara no se activa hasta que des tu consentimiento.
      </p>
      <Actions>
        <button type="button" className="btn-primary" data-testid="welcome-start" onClick={onStart} disabled={study.demo.enabled && study.demo.participationsLeft === 0}>
          Continuar
        </button>
      </Actions>
    </Panel>
  );
}

export function ConsentScreen({
  study,
  retentionDays,
  onAccept,
  onCancel,
}: {
  study: PublicStudyDto;
  retentionDays: number | null;
  onAccept: () => void;
  onCancel: () => void;
}) {
  const [checked, setChecked] = useState(false);
  return (
    <Panel title="Consentimiento informado" wide>
      <p className="text-sm text-slate-500">Versión del consentimiento: {study.consentVersion}</p>
      {study.consentText && <div className="whitespace-pre-wrap rounded-md bg-slate-50 p-4 text-sm">{study.consentText}</div>}
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="font-semibold text-slate-900">Finalidad</dt>
          <dd>
            Comparar, de forma exploratoria, cómo se distribuye la atención visual estimada sobre {study.stimulusKind === "video" ? "videos" : study.stimulusKind === "mixed" ? "imágenes y videos" : "fotografías"} entre
            grupos de edad.
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900">Qué se guarda</dt>
          <dd>
            Un código anónimo, tu edad en años, si usas lentes y cómo describes tu visión, datos técnicos de la pantalla y del
            navegador, la calidad de la calibración y las coordenadas estimadas de tu mirada con su tiempo. No se guarda tu
            nombre, correo, fecha de nacimiento, video, fotos ni audio.
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900">Responsable</dt>
          <dd>{study.dataController}</dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900">Conservación</dt>
          <dd>
            {retentionDays
              ? `Los datos se conservan hasta ${retentionDays} días y después se eliminan.`
              : "Los datos se conservan durante el periodo definido por el estudio y después se eliminan."}
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900">Retiro y eliminación</dt>
          <dd>
            Puedes detenerte en cualquier momento sin consecuencia alguna. Al terminar recibirás un código de participante;
            con él puedes pedir al equipo del estudio (contacto abajo) que elimine tus datos cuando quieras.
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900">Contacto</dt>
          <dd>{study.contactInfo}</dd>
        </div>
      </dl>
      <label className="flex items-start gap-3 rounded-md border border-slate-200 p-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          data-testid="consent-checkbox"
        />
        <span>
          He leído la información, acepto participar y autorizo el uso de la webcam para estimar mi mirada y el registro de
          esas estimaciones.
        </span>
      </label>
      <Actions>
        <button type="button" className="btn-primary" disabled={!checked} onClick={onAccept} data-testid="consent-accept">
          Acepto y continúo
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} data-testid="consent-cancel">
          No deseo participar
        </button>
      </Actions>
    </Panel>
  );
}

export interface ProfileData {
  ageYears: number;
  visionCorrection: VisionCorrection;
  selfReportedVision: SelfReportedVision;
  lighting: "buena" | "regular" | "mala" | "no_sabe";
  distanceCm: number | null;
}

export function ProfileScreen({
  study,
  onSubmit,
  onCancel,
}: {
  study: PublicStudyDto;
  onSubmit: (p: ProfileData) => void;
  onCancel: () => void;
}) {
  const [age, setAge] = useState("");
  const [vision, setVision] = useState<VisionCorrection | "">("");
  const [selfVision, setSelfVision] = useState<SelfReportedVision | "">("");
  const [lighting, setLighting] = useState<ProfileData["lighting"]>("buena");
  const [distance, setDistance] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [outOfRange, setOutOfRange] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const n = Number(age);
    if (!/^\d{1,3}$/.test(age.trim()) || !Number.isInteger(n) || n < 0 || n > 130) {
      setError("Escribe tu edad en años cumplidos (número entero).");
      return;
    }
    if (!vision || !selfVision) {
      setError("Responde las dos preguntas sobre visión (puedes elegir «Prefiero no decir»).");
      return;
    }
    if (!assignAgeGroup(study.groups, n)) {
      setOutOfRange(true);
      return;
    }
    const d = distance.trim() === "" ? null : Number(distance);
    if (d !== null && (!Number.isInteger(d) || d < 20 || d > 200)) {
      setError("La distancia aproximada debe ser un número entre 20 y 200 cm, o déjala vacía.");
      return;
    }
    onSubmit({ ageYears: n, visionCorrection: vision, selfReportedVision: selfVision, lighting, distanceCm: d });
  };

  if (outOfRange) {
    return (
      <Panel title="Gracias por tu interés">
        <p data-testid="age-out-of-range">
          Tu edad no está dentro de los rangos que este estudio compara (
          {study.groups.map((g) => `${g.minAge}–${g.maxAge}`).join(", ")} años). No se registró ningún dato tuyo.
        </p>
        <Actions>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Salir
          </button>
        </Actions>
      </Panel>
    );
  }

  return (
    <Panel title="Algunos datos (anónimos)">
      <p className="text-sm">No pedimos tu nombre ni tu fecha de nacimiento.</p>
      <form className="space-y-4" onSubmit={submit} noValidate>
        <div>
          <label className="label" htmlFor="age">
            Edad en años cumplidos
          </label>
          <input
            id="age"
            className="input max-w-32"
            inputMode="numeric"
            autoComplete="off"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            data-testid="age-input"
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="vision">
            ¿Usas lentes o lentes de contacto ahora?
          </label>
          <select
            id="vision"
            className="input"
            value={vision}
            onChange={(e) => setVision(e.target.value as VisionCorrection)}
            data-testid="vision-correction"
          >
            <option value="">Selecciona…</option>
            {Object.entries(VISION_CORRECTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="self-vision">
            ¿Cómo describirías tu visión?
          </label>
          <select
            id="self-vision"
            className="input"
            value={selfVision}
            onChange={(e) => setSelfVision(e.target.value as SelfReportedVision)}
            data-testid="self-reported-vision"
          >
            <option value="">Selecciona…</option>
            {Object.entries(SELF_REPORTED_VISION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="lighting">
            Iluminación de la habitación
          </label>
          <select id="lighting" className="input" value={lighting} onChange={(e) => setLighting(e.target.value as ProfileData["lighting"])}>
            <option value="buena">Buena</option>
            <option value="regular">Regular</option>
            <option value="mala">Mala</option>
            <option value="no_sabe">No sé</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="distance">
            Distancia aproximada a la pantalla en cm (opcional)
          </label>
          <input
            id="distance"
            className="input max-w-32"
            inputMode="numeric"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
            placeholder="60"
          />
        </div>
        {error && <ErrorText>{error}</ErrorText>}
        <Actions>
          <button type="submit" className="btn-primary" data-testid="profile-submit">
            Continuar
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
        </Actions>
      </form>
    </Panel>
  );
}
